import { useState } from "react";
import { Sword, Shirt, Package, Sparkles, Target, TrendingUp, Briefcase, User, Trophy, Coffee, Wine, Music, Map, Key, Star, Crown, Headphones, Megaphone, Shield, Hammer, Award, Camera, Cookie, Beer, BookOpen } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AchievementBadges from "@/components/AchievementBadges";
import NpcRoster from "@/components/NpcRoster";

const ICON_MAP = {
  Sword, Shirt, Package, Sparkles, Target, TrendingUp, Briefcase, Trophy,
  Coffee, Wine, Music, Map, Key, Star, Crown, Headphones, Megaphone, Shield,
  Hammer, Award, Camera, Cookie, Beer,
};

// v14.1: Loot-Journal — Quellenbezeichnungen im Modal
const SOURCE_LABEL = {
  gm: "Story-Drop",
  fallback: "Fundstück",
  milestone: "Meilenstein",
  npc_arc: "NPC-Arc Belohnung",
  level_up: "Level-Up-Belohnung",
  start: "Start-Ausrüstung",
};

const INVENTORY_LIMIT = 10;

export default function CharacterSheet({ profile, onChange }) {
  // Progressive RPG-Kurve — muss mit backend xp_required_for_level() übereinstimmen
  const xpForNext = profile.level * (profile.level + 1) * 50;
  const xpPercent = Math.min(100, Math.round((profile.xp / xpForNext) * 100));
  const [showAllInventory, setShowAllInventory] = useState(false);
  const [detail, setDetail] = useState(null); // {kind:'skill'|'item', name, description, bonus, icon}

  const skillBonus = (profile.skills || []).reduce((sum, s) => sum + (s.dice_bonus || 0), 0);
  const itemBonus = (profile.inventory || []).reduce((sum, i) => sum + (i.bonus || 0), 0);
  const totalBonus = skillBonus + itemBonus;

  const inventory = profile.inventory || [];
  const hiddenInventory = Math.max(0, inventory.length - INVENTORY_LIMIT);
  const visibleInventory = showAllInventory || hiddenInventory === 0
    ? inventory
    : inventory.slice(-INVENTORY_LIMIT);

  return (
    <div className="space-y-6" data-testid="character-sheet">
      {/* Header */}
      <div className="bg-hammerl-card border-2 border-hammerl-ink shadow-brutal-sm p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil mb-1">Reiter-Akte</div>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-hammerl-corpblue text-white flex items-center justify-center font-bold">
            <User className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg truncate" data-testid="character-name">{profile.name}</div>
            <div className="font-mono text-xs text-hammerl-pencil">Hammerl-Talent ID: {profile.id.slice(0, 8)}</div>
          </div>
        </div>
      </div>

      {/* Level / XP */}
      <div className="bg-hammerl-card border-2 border-hammerl-ink p-4">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">Galopp-Level</div>
            <div className="font-mono font-bold text-3xl" data-testid="character-level">LVL {profile.level}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">XP</div>
            <div className="font-mono font-bold text-lg" data-testid="character-xp">{profile.xp} / {xpForNext}</div>
          </div>
        </div>
        <Progress value={xpPercent} className="h-3 rounded-none bg-hammerl-sidebar border border-hammerl-ink [&>div]:bg-hammerl-corpblue" />
        <div className="font-handwriting text-base text-hammerl-blue mt-2 -rotate-1 inline-block">
          {xpPercent >= 100 ? "Bereit für Level-Up! 🏆" : `noch ${xpForNext - profile.xp} XP bis zur Beförderung`}
        </div>
      </div>

      {/* Galopp-Streak */}
      <div className="bg-hammerl-card border-2 border-hammerl-ink p-4" data-testid="streak-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">Galopp-Streak</div>
            <div className="flex items-baseline gap-2">
              <span className="font-mono font-bold text-3xl" data-testid="streak-current">
                {profile.streak_current || 0}
              </span>
              <span className="font-mono text-xs text-hammerl-pencil">
                Tag{(profile.streak_current || 0) === 1 ? "" : "e"} in Folge
              </span>
            </div>
          </div>
          <div className="text-4xl -rotate-6 select-none" aria-hidden>🐴</div>
        </div>
        <div className="font-handwriting text-base text-hammerl-blue mt-1 -rotate-1 inline-block">
          {(profile.streak_current || 0) === 0
            ? "Starte deinen Streak — Hammerl beobachtet."
            : `Persönlicher Rekord: ${profile.streak_max || profile.streak_current} Tage`}
        </div>
      </div>

      {/* Skills */}
      <div className="bg-hammerl-card border-2 border-hammerl-ink">
        <div className="border-b-2 border-hammerl-ink px-4 py-2 bg-hammerl-highlight font-mono text-xs uppercase tracking-widest flex items-center justify-between text-hammerl-paper">
          <span>Skill-Matrix</span>
          <span className="flex items-center gap-2">
            {totalBonus > 0 && (
              <span className="px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-card text-hammerl-ink font-bold" data-testid="total-dice-bonus">
                +{totalBonus}% Wurf
              </span>
            )}
            <span className="text-hammerl-paper/70">{profile.skills.length}</span>
          </span>
        </div>
        <div className="p-3 space-y-1">
          {profile.skills.map((s, i) => (
            <button
              key={s.name || `skill-${i}`}
              type="button"
              onClick={() => setDetail({ kind: "skill", name: s.name, description: s.description, bonus: s.dice_bonus || 0, icon: "Sparkles" })}
              className="w-full text-left p-1.5 hover:bg-hammerl-sidebar focus:bg-hammerl-sidebar focus:outline-none transition-colors border-b border-hammerl-lines last:border-b-0"
              data-testid={`skill-${i}`}
              aria-label={s.name}
            >
              <div className="font-bold text-sm flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <Sparkles className="w-3.5 h-3.5 text-hammerl-corpblue shrink-0" />
                  <span className="truncate">{s.name}</span>
                </span>
                {(s.dice_bonus || 0) > 0 && (
                  <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-mixed font-mono text-[10px] font-bold text-hammerl-paper">
                    +{s.dice_bonus}%
                  </span>
                )}
              </div>
              {s.description && (
                <div className="text-xs text-hammerl-pencil ml-5 truncate">{s.description}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Inventory */}
      <div className="bg-hammerl-card border-2 border-hammerl-ink">
        <div className="border-b-2 border-hammerl-ink px-4 py-2 bg-hammerl-mixed font-mono text-xs uppercase tracking-widest flex items-center justify-between text-hammerl-paper">
          <span>Ausrüstungs-Inventar</span>
          <span className="text-hammerl-paper/70">{visibleInventory.length} / {inventory.length}</span>
        </div>
        <div className="p-3 space-y-1">
          {visibleInventory.map((item, i) => {
            const Icon = ICON_MAP[item.icon] || Package;
            return (
              <button
                key={`${item.name}-${i}`}
                type="button"
                onClick={() => setDetail({
                  kind: "item",
                  name: item.name,
                  description: item.description,
                  bonus: item.bonus || 0,
                  icon: item.icon || "Package",
                  found_at: item.found_at || "",
                  source_kind: item.source_kind || "",
                  acquired_at: item.acquired_at || "",
                })}
                className="w-full text-left flex items-start gap-3 p-1.5 hover:bg-hammerl-sidebar focus:bg-hammerl-sidebar focus:outline-none transition-colors"
                data-testid={`inventory-item-${i}`}
                aria-label={item.name}
              >
                <div className="w-9 h-9 bg-hammerl-sidebar border border-hammerl-ink flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm flex items-center justify-between gap-2">
                    <span className="truncate">{item.name}</span>
                    {(item.bonus || 0) > 0 && (
                      <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-highlight font-mono text-[10px] font-bold text-hammerl-ink">
                        +{item.bonus}%
                      </span>
                    )}
                  </div>
                  {item.description && <div className="text-xs text-hammerl-pencil truncate">{item.description}</div>}
                </div>
              </button>
            );
          })}
          {hiddenInventory > 0 && (
            <button
              type="button"
              onClick={() => setShowAllInventory((v) => !v)}
              className="w-full font-mono text-[10px] uppercase tracking-widest underline decoration-hammerl-ink underline-offset-2 hover:text-hammerl-corpblue transition-colors py-1"
              data-testid="toggle-inventory-btn"
            >
              {showAllInventory ? "Nur letzte 10 zeigen" : `+${hiddenInventory} ältere anzeigen`}
            </button>
          )}
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-hammerl-card border-2 border-hammerl-ink">
        <div className="border-b-2 border-hammerl-ink px-4 py-2 bg-hammerl-corpblue text-white font-mono text-xs uppercase tracking-widest flex items-center justify-between">
          <span>Auszeichnungen</span>
          <span className="text-white/70">{profile.achievements?.length || 0}</span>
        </div>
        <AchievementBadges achievements={profile.achievements || []} />
      </div>

      {/* v14.1: Aktiver NPC-Neben-Arc (nur sichtbar wenn einer läuft) */}
      {profile.active_npc_arc && (
        <div className="bg-hammerl-card border-2 border-hammerl-ink" data-testid="npc-arc-widget">
          <div className="border-b-2 border-hammerl-ink px-4 py-2 bg-hammerl-highlight text-hammerl-paper font-mono text-xs uppercase tracking-widest flex items-center justify-between">
            <span>NPC-Neben-Arc</span>
            <span className="text-hammerl-paper/80 text-[10px]">
              {Math.min(profile.active_npc_arc.progress || 0, profile.active_npc_arc.total_steps || 6)}
              /{profile.active_npc_arc.total_steps || 6}
            </span>
          </div>
          <div className="p-4 space-y-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-corpblue">
              mit {profile.active_npc_arc.npc || "?"}
            </div>
            <div className="font-bold text-sm text-hammerl-ink leading-tight" data-testid="npc-arc-title">
              {profile.active_npc_arc.title}
            </div>
            <p className="text-xs text-hammerl-pencil leading-relaxed">
              {profile.active_npc_arc.description}
            </p>
            <div className="w-full h-2 bg-hammerl-sidebar border-2 border-hammerl-ink overflow-hidden">
              <div
                className="h-full bg-hammerl-corpblue"
                style={{
                  width: `${Math.min(100, Math.round(((profile.active_npc_arc.progress || 0) / Math.max(1, profile.active_npc_arc.total_steps || 6)) * 100))}%`
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* NPC-Roster: klickbare Portraits + Beziehungs-Bars */}
      <NpcRoster
        relationships={profile.relationships}
        completedArcs={profile.completed_npc_arcs || []}
      />

      {/* Stats footer */}
      <div className="text-[10px] font-mono uppercase tracking-widest text-hammerl-pencil text-center">
        Präsentiert von{" "}
        <a
          href="https://hobby-horsing-hammerl.de"
          target="_blank"
          rel="noopener noreferrer"
          className="text-hammerl-corpblue hover:text-hammerl-highlight underline decoration-dotted underline-offset-2 transition-colors"
          data-testid="sponsor-link"
        >
          hobby-horsing-hammerl.de
        </a>
      </div>

      {/* Detail-Dialog für Skills + Items — zentriert, mobile-freundlich */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent
          className="max-w-md rounded-none border-2 border-hammerl-ink shadow-brutal bg-hammerl-card p-0 overflow-hidden"
          data-testid="entry-detail-dialog"
        >
          {detail && (() => {
            const Icon = ICON_MAP[detail.icon] || Package;
            const labelText = detail.kind === "skill" ? "Fähigkeit" : "Ausrüstung";
            const accentClass = detail.kind === "skill" ? "bg-hammerl-highlight" : "bg-hammerl-mixed";
            return (
              <>
                <div className={`px-5 py-4 border-b-2 border-hammerl-ink ${accentClass} text-hammerl-paper flex items-start gap-3`}>
                  <div className="w-11 h-11 bg-hammerl-card border-2 border-hammerl-ink flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-hammerl-ink" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-paper/70">{labelText}</div>
                    <DialogTitle className="font-bold text-lg leading-tight break-words" data-testid="entry-detail-name">
                      {detail.name}
                    </DialogTitle>
                    {detail.bonus > 0 && (
                      <span className="mt-1 inline-flex items-center px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-paper text-hammerl-ink font-mono text-[10px] font-bold">
                        +{detail.bonus}% Wurf-Chance
                      </span>
                    )}
                  </div>
                </div>
                <div className="px-5 py-4">
                  {detail.description ? (
                    <DialogDescription className="text-sm text-hammerl-ink leading-relaxed break-words whitespace-pre-wrap" data-testid="entry-detail-description">
                      {detail.description}
                    </DialogDescription>
                  ) : (
                    <DialogDescription className="text-sm text-hammerl-pencil italic">
                      Keine Beschreibung hinterlegt.
                    </DialogDescription>
                  )}
                  {/* v14.1: Loot-Journal — Fundort-Story */}
                  {detail.kind === "item" && (detail.found_at || detail.source_kind) && (
                    <div
                      className="mt-4 pt-3 border-t-2 border-dashed border-hammerl-pencil"
                      data-testid="loot-journal-section"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <BookOpen className="w-3.5 h-3.5 text-hammerl-corpblue" />
                        <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">Loot-Journal · Fundort</div>
                        {detail.source_kind && (
                          <span className="ml-auto inline-flex items-center px-1.5 py-0.5 border border-hammerl-ink bg-hammerl-sidebar font-mono text-[9px] uppercase tracking-wider text-hammerl-ink">
                            {SOURCE_LABEL[detail.source_kind] || detail.source_kind}
                          </span>
                        )}
                      </div>
                      {detail.found_at ? (
                        <p className="text-sm text-hammerl-ink italic leading-relaxed" data-testid="loot-journal-found-at">
                          „{detail.found_at}"
                        </p>
                      ) : (
                        <p className="text-xs text-hammerl-pencil italic">Herkunft unklar — verloren im Papierkram.</p>
                      )}
                      {detail.acquired_at && (
                        <p className="mt-1 font-mono text-[10px] text-hammerl-pencil">
                          Erhalten: {new Date(detail.acquired_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
